import { SETUP_STEPS, SIGNAL_KEY_BY_STEP } from '@/constants/setupSteps';
import { isFeatureEnabled } from '@/lib/feature-flag';
import { usePlanStore } from '@/stores';
import type { SetupProgress, SetupSignals, SetupStep, SetupStepId } from '@/types';

/**
 * Steps whose feature flag is on, plus every unflagged step. The one place the
 * gate is applied, so the checklist, the revalidation map and the store's
 * fetchers cannot disagree about which steps exist.
 *
 * Not filtered by `requiredFeature`: plan-gated steps still render, locked.
 */
function enabledSteps() {
  return SETUP_STEPS.filter(step => !step.flag || isFeatureEnabled(step.flag));
}

/** False while a step's feature flag is off — nothing should render or fetch it. */
export function isStepEnabled(stepId: SetupStepId): boolean {
  return enabledSteps().some(step => step.id === stepId);
}

const currentFeatures = () => usePlanStore.getState().subscription?.features;

/** Required plan feature not confirmed (including an unloaded plan): never fetched or counted. */
function isStepGated(
  stepId: SetupStepId,
  features: readonly string[] | undefined = currentFeatures()
): boolean {
  const step = SETUP_STEPS.find(s => s.id === stepId);
  if (!step?.requiredFeature) return false;
  return !(features?.includes(step.requiredFeature) ?? false);
}

/** Gated by a plan we actually know; an unloaded plan shows no lock (fail-open). */
export function isStepLocked(
  stepId: SetupStepId,
  features: readonly string[] | undefined = currentFeatures()
): boolean {
  return features !== undefined && isStepGated(stepId, features);
}

/** The only steps `setupStore.refresh` may probe: a gated route would 402 and pop the upgrade modal unprompted. */
export function isStepFetchable(stepId: SetupStepId): boolean {
  return isStepEnabled(stepId) && !isStepGated(stepId);
}

/**
 * Completion predicate per step. Kept pure and in one place so the top bar, the
 * dashboard widget and the tests can never disagree about what "done" means.
 */
export function isStepComplete(stepId: SetupStepId, signals: SetupSignals): boolean {
  switch (stepId) {
    case 'brand-dna':
      // The BE only reports 100 once identity, goals, content and voice are all
      // filled, so this is the whole definition of a complete Brand DNA.
      return signals.brandDnaPercent === 100;
    case 'knowledge-base':
      // `indexed` (not merely uploaded) — an unprocessed document contributes
      // nothing to generation, so counting it would overstate readiness.
      return (signals.knowledgeBaseIndexedCount ?? 0) > 0;
    case 'competitors':
      return (signals.competitorCount ?? 0) > 0;
    case 'review-queue':
      return (signals.reviewedPostCount ?? 0) > 0;
    case 'chat':
      return (signals.chatConversationCount ?? 0) > 0;
    case 'telegram':
      // Strict `=== true`: the signal is a tri-state, and both `null` (not read
      // yet) and a failed read's fallback must read as unlinked.
      return signals.telegramLinked === true;
  }
}

/** True once the signal a step depends on has been fetched at least once. */
export function isStepKnown(stepId: SetupStepId, signals: SetupSignals): boolean {
  return signals[SIGNAL_KEY_BY_STEP[stepId]] !== null;
}

/** Route prefix → the step it can change, for route-based revalidation. */
export function stepForPath(pathname: string): SetupStepId | undefined {
  for (const step of enabledSteps()) {
    const routes = [step.route, ...(step.alsoAffectedBy ?? [])];
    if (routes.some(route => pathname.startsWith(route))) return step.id;
  }
  return undefined;
}

/**
 * Build the full view model the setup surfaces render from. React callers pass
 * their subscribed `features` so a plan change re-derives the locks.
 */
export function deriveSetupProgress(
  signals: SetupSignals,
  features: readonly string[] | undefined = currentFeatures()
): SetupProgress {
  const flags = enabledSteps().map(def => ({
    def,
    completed: isStepComplete(def.id, signals),
    known: isStepKnown(def.id, signals),
    gated: isStepGated(def.id, features),
    locked: isStepLocked(def.id, features),
  }));

  // Optional steps render as rows but never count: they are offered extras, and
  // one joining the denominator would un-complete every finished account.
  // Gated steps are excluded too: their signal is never fetched, so counting
  // them would pin the checklist below 100% and stall `hydrated`.
  const nextIndex = flags.findIndex(f => !f.completed && !f.def.optional && !f.gated);
  const steps: SetupStep[] = flags.map((f, i) => ({
    ...f.def,
    completed: f.completed,
    known: f.known,
    locked: f.locked,
    isNext: i === nextIndex,
  }));

  const counted = steps.filter((s, i) => !s.optional && !flags[i].gated);
  const completedCount = counted.filter(s => s.completed).length;
  const totalCount = counted.length;

  return {
    steps,
    completedCount,
    totalCount,
    percent: Math.round((completedCount / totalCount) * 100),
    nextStep: nextIndex === -1 ? null : steps[nextIndex],
    // Counted steps only: an optional or locked step whose signal never
    // resolves must not hold the whole checklist off the screen.
    hydrated: counted.every(s => s.known),
    isComplete: completedCount === totalCount,
  };
}
